import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, Lock, Users, FileText, Clock, ChevronRight, CheckCircle, AlertCircle, Phone, Mail, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
    const [scrollY, setScrollY] = useState(0);
    const [activeSection, setActiveSection] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);

            const elements = document.querySelectorAll('.fade-in');
            elements.forEach((el, index) => {
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight * 0.8) {
                    setTimeout(() => {
                        el.classList.add('animate-in');
                    }, index * 100);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const privacySections = [
        {
            id: 'introduction',
            title: 'Introduction',
            icon: Shield,
            content: [
                'Navam Biz Solutions ("we," "us," or "our") values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (https://www.navambizsolutions.com) or use our services. By accessing or using our website and services, you agree to the terms outlined in this Privacy Policy.',
                'This policy describes how we collect, use, disclose, and transfer personal information through our websites and applications. It applies to visitors of our platform or those whose information Navam Biz Solutions receives.'
            ]
        },
        {
            id: 'collection',
            title: 'Types of Personal Information Collected',
            icon: Eye,
            content: [
                '"Personal Information" refers to any data that identifies you as an individual. This includes, but is not limited to:'
            ],
            list: [
                'Name, email address, password, contact details, and associated company/organization when you sign up for alerts on the platform.',
                'Information typically found in a resume (e.g., work experience, educational qualifications, etc.) when registering.',
                'Details about the services you use and how you use them.',
                'Survey responses and preferences when participating in surveys.',
                'Technical information from your device (e.g., IP address, browser type, device model) when accessing the platform.',
                'Cookies, usage data, and analytics information collected automatically through your interactions with the website.'
            ]
        },
        {
            id: 'usage',
            title: 'How We Use Your Personal Information',
            icon: Users,
            content: [
                'We process your personal information fairly and only when we have a lawful reason to do so. Some key uses include:'
            ],
            list: [
                'Providing job alerts, search results, and personalized recommendations.',
                'Improving platform content, navigation, and functionality for better user experience.',
                'Monitoring usage patterns and performance to enhance services.',
                'Preventing, detecting, and investigating security breaches, fraud, or violations of our terms.',
                'Conducting market research and analysis to understand user needs.',
                'Communicating with you for service-related or promotional purposes.',
                'Establishing legal rights or defending against legal claims.'
            ]
        },
        {
            id: 'sharing',
            title: 'Information Sharing and Disclosure',
            icon: Lock,
            content: [
                'Navam Biz Solutions does not sell your information. In certain situations, information may be shared with:'
            ],
            list: [
                'Affiliates and group companies for internal business operations.',
                'Potential recruiters or clients whose job openings match your profile.',
                'Third-party service providers and vendors involved in platform support, analytics, or communication.',
                'Governmental or legal authorities, when required by applicable law, court order, or to establish or defend legal claims.',
                'Partners with whom we conduct joint marketing or promotional campaigns (with your consent).'
            ]
        },
        {
            id: 'security',
            title: 'Data Security',
            icon: Lock,
            content: [
                'We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, use, disclosure, alteration, or destruction.',
                'However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee its absolute security.'
            ]
        },
        {
            id: 'retention',
            title: 'Retention of Personal Information',
            icon: Clock,
            content: [
                'We retain personal data only as long as necessary to fulfill the purposes outlined in this Privacy Policy, including legal, accounting, or reporting requirements.',
                'When personal data is no longer required, we ensure it is securely deleted, anonymized, or archived in accordance with applicable laws.'
            ]
        },
        {
            id: 'control',
            title: 'Controlling Your Information',
            icon: CheckCircle,
            content: [
                'You have the right to access, update, or delete your personal information stored with us. To exercise these rights, contact our support team at the provided email.',
                'You may also opt out of marketing communications or withdraw consent for specific data uses, where applicable.',
                'We may require identity verification to process certain requests and ensure data security.'
            ]
        },
        {
            id: 'changes',
            title: 'Changes to this Privacy Policy',
            icon: AlertCircle,
            content: [
                'Navam Biz Solutions may periodically update this Privacy Policy to reflect changes in laws, practices, or services.',
                'We encourage you to review this policy regularly. Continued use of our platform following updates constitutes your acceptance of the revised terms.'
            ]
        },

    ];


    const keyPoints = [
        {
            icon: Shield,
            title: 'Data Protection',
            desc: 'Your information is protected with industry-standard security measures'
        },
        {
            icon: Lock,
            title: 'Secure Storage',
            desc: 'All personal data is encrypted and stored securely'
        },
        {
            icon: CheckCircle,
            title: 'Your Rights',
            desc: 'Access, modify, or delete your data anytime'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Floating Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute top-20 -left-20 w-96 h-96 bg-[#0e6d87]/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * 0.2}px)` }}
                />
                <div
                    className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"
                    style={{ transform: `translateY(${scrollY * -0.3}px)` }}
                />
            </div>

            <section className="relative py-16 px-4 sm:px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    {/* Hero Section */}
                    <div className="text-center mb-16 fade-in">
                        <div className="inline-block mb-6">
                            <span className="px-4 py-2 bg-[#0e6d87]/10 text-[#0e6d87] rounded-full text-sm font-medium">
                                Your Privacy Matters
                            </span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-800 mb-6 leading-tight">
                            Privacy Policy
                        </h1>
                        <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-600 leading-relaxed">
                            We are committed to protecting your privacy and ensuring the security of your personal information.
                            Learn how we collect, use, and safeguard your data.
                        </p>
                    </div>

                    {/* Key Privacy Points */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                        {keyPoints.map((point, index) => {
                            const IconComponent = point.icon;
                            return (
                                <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center fade-in">
                                    <div className="w-12 h-12 bg-[#0e6d87] rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <IconComponent className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{point.title}</h3>
                                    <p className="text-sm text-gray-600">{point.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Main Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Table of Contents */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8 fade-in">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Contents</h3>
                                <nav className="space-y-2">
                                    {privacySections.map((section, index) => {
                                        const IconComponent = section.icon;
                                        return (
                                            <a
                                                key={index}
                                                href={`#${section.id}`}
                                                className={`flex items-center text-sm p-2 rounded-lg transition-colors duration-200 ${activeSection === section.id
                                                    ? 'bg-[#0e6d87] text-white'
                                                    : 'text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                onClick={() => setActiveSection(section.id)}
                                            >
                                                <IconComponent className="w-4 h-4 mr-2 flex-shrink-0" />
                                                <span className="truncate">{section.title}</span>
                                            </a>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>

                        {/* Policy Content */}
                        <div className="lg:col-span-3 space-y-8">
                            {privacySections.map((section, index) => {
                                const IconComponent = section.icon;
                                return (
                                    <div
                                        key={index}
                                        id={section.id}
                                        className="bg-white rounded-lg shadow-md p-6 lg:p-8 fade-in"
                                    >
                                        <div className="flex items-center mb-6">
                                            <div className="w-10 h-10 bg-[#0e6d87] rounded-lg flex items-center justify-center mr-4">
                                                <IconComponent className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-2xl font-semibold text-gray-800">
                                                    {index + 1}. {section.title}
                                                </h2>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            {section.content.map((paragraph, pIndex) => (
                                                <p key={pIndex} className="text-gray-600 leading-relaxed">
                                                    {paragraph}
                                                </p>
                                            ))}

                                            {section.list && (
                                                <ul className="space-y-3 mt-4">
                                                    {section.list.map((item, lIndex) => (
                                                        <li key={lIndex} className="flex items-start">
                                                            <ChevronRight className="w-4 h-4 text-[#0e6d87] mr-2 mt-0.5 flex-shrink-0" />
                                                            <span className="text-gray-600">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Last Updated & Contact */}
                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Last Updated */}
                        <div className="bg-gray-100 rounded-lg p-6 fade-in">
                            <div className="flex items-center mb-4">
                                <Clock className="w-5 h-5 text-[#0e6d87] mr-2" />
                                <h3 className="font-semibold text-gray-800">Last Updated</h3>
                            </div>
                            <p className="text-gray-600 text-sm">
                                This Privacy Policy was last updated on <strong>December 2024</strong>.
                                We may update this policy periodically to reflect changes in our practices or legal requirements.
                            </p>
                        </div>

                        {/* Contact for Privacy Questions */}
                        <div className="bg-white rounded-lg shadow-md p-6 fade-in">
                            <div className="flex items-center mb-4">
                                <Mail className="w-5 h-5 text-[#0e6d87] mr-2" />
                                <h3 className="font-semibold text-gray-800">Privacy Questions?</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4">
                                If you have any questions about this Privacy Policy or our data practices, please contact us:
                            </p>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center text-gray-600">
                                    <Mail className="w-4 h-4 text-[#0e6d87] mr-2" />
                                    <a href="mailto:info@navambizsolutions.com" className="text-[#0e6d87] hover:underline">
                                        info@navambizsolutions.com
                                    </a>
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Phone className="w-4 h-4 text-[#0e6d87] mr-2" />
                                    <a href="tel:+917276156055" className="text-[#0e6d87] hover:underline">
                                        +91 72761 56055
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="mt-16 text-center bg-gray-100 rounded-lg p-8 fade-in">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                            Questions About Our Privacy Practices?
                        </h2>
                        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                            We're here to help. Contact our team for any privacy-related questions or concerns.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/contact')}
                                className="inline-flex items-center px-5 py-2.5 bg-[#0e6d87] text-white font-semibold rounded-lg shadow hover:shadow-md hover:bg-[#0b566a] transition-all duration-300"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
                .fade-in {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                
                .animate-in {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                html {
                    scroll-behavior: smooth;
                }
            `}</style>
        </div>
    );
};

export default PrivacyPolicy;